import React from "react";

/**
 * Segmented language control. A click anywhere advances to the next option.
 *
 * @param {Object} props Component props.
 * @param {string} props.language Active language code.
 * @param {Array<{code: string, label: string}>} props.options Languages to show, in order.
 * @param {Function} props.onChange Called with the next language code.
 * @param {boolean} [props.disabled=false] Whether language changes are in flight.
 * @param {"compact"|"touch"|"pill"|"full"} [props.variant="compact"] Visual size.
 * @param {Function} [props.getLabel] Button text for an option. Defaults to `option.label`.
 * @param {string} [props.ariaLabel] Accessible name for the control.
 * @returns {JSX.Element} Language toggle.
 */
const LanguageToggle = ({
  language,
  options,
  onChange,
  disabled = false,
  variant = "compact",
  getLabel,
  ariaLabel,
}) => {
  const labelFor = getLabel || ((option) => option.label);
  const activeCode = String(language || "").trim().toLowerCase();
  const activeIndex = options.findIndex((option) => option.code === activeCode);

  /**
   * Select the option after the active one, wrapping to the start.
   *
   * @returns {void}
   */
  const selectNext = () => {
    if (!options.length) {
      return;
    }
    const nextIndex = activeIndex === -1 ? 0 : (activeIndex + 1) % options.length;
    const next = options[nextIndex];
    onChange(next.code);
  };

  const segmentClass = (selected) => {
    if (variant === "touch") {
      return `min-h-[52px] touch-manipulation rounded-lg border-2 text-base font-semibold transition ${
        selected
          ? "border-primary bg-primary text-white"
          : "border-gray-300 bg-white text-gray-700"
      }`;
    }
    if (variant === "full") {
      return `flex min-h-[52px] flex-1 items-center justify-center touch-manipulation px-3 text-base font-semibold leading-none transition ${
        selected ? "bg-primary text-white" : "bg-white text-primary"
      }`;
    }
    if (variant === "pill") {
      return `flex h-4 min-w-[1.5rem] items-center justify-center px-1.5 text-[10px] font-semibold leading-none transition ${
        selected ? "bg-primary text-white" : "bg-white text-primary"
      }`;
    }
    return `flex h-5 items-center px-2.5 text-xs font-semibold transition ${
      selected
        ? "bg-primary text-white"
        : "bg-white text-gray-600"
    }`;
  };

  const controlClass = {
    touch: "mt-3 grid w-full gap-3",
    full: "flex w-full items-stretch overflow-hidden rounded-full border-2 border-primary p-0",
    pill: "inline-flex items-center cursor-pointer overflow-hidden rounded-full border border-primary p-0 leading-none outline-none focus:outline-none focus-visible:outline-none",
    compact: "inline-flex overflow-hidden rounded-md border border-gray-300",
  }[variant];

  return (
    <button
      type="button"
      disabled={disabled}
      aria-label={ariaLabel}
      onClick={selectNext}
      className={`${controlClass} cursor-pointer disabled:cursor-default`}
      style={
        variant === "touch"
          ? {
              gridTemplateColumns: `repeat(${Math.max(options.length, 1)}, minmax(0, 1fr))`,
            }
          : undefined
      }
    >
      {options.map((option) => (
        <span
          key={option.code}
          className={segmentClass(activeCode === option.code)}
        >
          {labelFor(option)}
        </span>
      ))}
    </button>
  );
};

export default LanguageToggle;
